import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SocketActions } from '@store/socketSaga'
import { call, put, select } from 'redux-saga/effects'

import { NestClient } from '@/nest'
import {
  type CommentReadsActions,
  InternalCommentReadsActions,
} from '../actions'
import { BASELINE_TARGET } from '../constants'
import {
  readSince,
  selectCommentReads,
  selectLatestForeignCreatedAt,
} from '../selectors'
import type { CommentReadsState } from '../types'

// The mark is the `createdAt` of the newest foreign comment on screen, not the
// clock: the server never has to compare its time with the browser's, and a
// comment that arrives while the object stays open earns a fresh mark
export function* markRead({
  payload: { target },
}: ReturnType<typeof CommentReadsActions.markRead>) {
  const reads: CommentReadsState = yield select(selectCommentReads)

  // no baseline — a guest, or the list has not arrived yet
  if (reads[BASELINE_TARGET] === undefined) {
    return
  }

  const latest: number | null = yield select(
    selectLatestForeignCreatedAt(target)
  )

  if (latest === null || latest <= readSince(reads, target)) {
    return
  }

  const projectId: ID = yield select(migrationProjectSelector.getProjectId)

  const mark = InternalCommentReadsActions.update({ target, readAt: latest })

  yield put(mark)

  // the same mark to this user's other tabs and devices, through their room
  yield put(SocketActions.emit({ eventName: 'comment-read', payload: mark }))

  try {
    yield call(NestClient.projects.commentReads.mark, projectId, target, {
      readAt: latest,
    })
  } catch (error) {
    console.error('Error marking comments read:', error)
  }
}
